import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../../supabase/supabase";

export default function DirectPromptGenerator() {
  const [isLoading, setIsLoading] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim()) {
      toast({
        title: "Missing information",
        description: "Please provide a title for your prompt",
        variant: "destructive",
      });
      return;
    }

    if (!description.trim()) {
      toast({
        title: "Missing information",
        description:
          "Please provide a description of the agent you want to create",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      // Get current user
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error("User not authenticated");

      // Get user's organization
      const { data: userOrgData, error: userOrgError } = await supabase
        .from("users")
        .select("organization_id")
        .eq("id", userData.user.id)
        .single();

      if (userOrgError) throw userOrgError;

      // First create a placeholder prompt
      const { data: promptData, error: promptError } = await supabase
        .from("prompts")
        .insert([
          {
            title,
            description: description,
            user_id: userData.user.id,
            organization_id: userOrgData.organization_id,
          },
        ])
        .select()
        .single();

      if (promptError) throw promptError;

      // Call the edge function to generate the prompt with Claude
      const { data, error } = await supabase.functions.invoke(
        "generate_prompt_claude",
        {
          body: {
            promptId: promptData.id,
            description,
          },
        },
      );

      if (error) throw error;

      toast({
        title: "Success",
        description:
          "Your prompt is being generated. You'll be redirected to view it.",
      });

      // Navigate to the prompt detail page
      navigate(`/prompt-engine/prompt/${promptData.id}`);
    } catch (error: any) {
      console.error("Error generating prompt:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to generate prompt",
        variant: "destructive",
      });
      setIsLoading(false);
    }
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>Generate Prompt Directly</CardTitle>
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Prompt Title</Label>
            <Input
              id="title"
              placeholder="E.g., Customer Support Agent"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={isLoading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">
              Describe the agent you want to create
            </Label>
            <Textarea
              id="description"
              placeholder="Describe the purpose, tone, and capabilities of the agent you want to create. For example: I need a customer support agent that can handle product inquiries, process returns, and maintain a friendly, professional tone."
              className="min-h-[200px]"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={isLoading}
            />
          </div>
        </CardContent>
        <CardFooter>
          <Button type="submit" disabled={isLoading} className="w-full">
            {isLoading ? "Generating..." : "Generate Prompt"}
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
}
